import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { DashboardSidebar } from './DashboardSidebar';
import { DashboardHeader } from './DashboardHeader';
import { MobileBottomNav } from './MobileBottomNav';
import { OfflineIndicator } from '@/components/pwa/OfflineIndicator';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { Loader2 } from 'lucide-react';
import { TaskSuggestionsDialog } from '@/components/tasks/TaskSuggestionsDialog';
import { TaskSuggestionsIndicator } from '@/components/tasks/TaskSuggestionsIndicator';
import { useAutoTaskSuggestions } from '@/hooks/useAutoTaskSuggestions';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, loading } = useAuth();
  const isMobile = useIsMobile();
  
  // Enable auto task suggestions
  useAutoTaskSuggestions();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <SidebarProvider defaultOpen={!isMobile}>
      <div className="h-screen flex w-full bg-muted/30">
        {!isMobile && <DashboardSidebar />}
        <SidebarInset className="flex-1 flex flex-col min-w-0">
          <DashboardHeader />
          <OfflineIndicator />
          <main className={cn(
            "flex-1 p-6 flex flex-col overflow-auto",
            isMobile && "pb-20 px-4" // Add padding for bottom nav and reduce horizontal padding
          )}>
            {children}
          </main>
        </SidebarInset>
      </div>
      {isMobile && <MobileBottomNav />}
      <TaskSuggestionsDialog />
      <TaskSuggestionsIndicator />
    </SidebarProvider>
  );
}